import { HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Platform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import {
  BehaviorSubject,
  from,
  Observable,
  of,
  Subscription,
  throwError,
} from "rxjs";
import {
  catchError,
  delay,
  filter,
  map,
  retryWhen,
  switchMap,
  take,
  tap,
  timeout,
} from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import { Plant } from "src/model/class/general classes/plant";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { currentUserData } from "src/state/selectors/authentication.selectors";
import { selectedPlant } from "src/state/selectors/plant.selectors";
import { AuthenticationService } from "./authentication.service";
import { DpAuthenticationService } from "./dp-authentication.service";
import { LoggerService } from "./logger.service";
import { ThemeService } from "./theme.service";

@Injectable({
  providedIn: "root",
})

/**
 * {@link AuthenticationInterceptorService} implements {@link HttpInterceptor} and manage HTTP modifier
 *
 */
export class AuthenticationInterceptorService implements HttpInterceptor {
  private _logContext: string = "Authentication-interceptor";

  //#region Subcriptions to "_store.select(...)" Observables
  private _selectCurrentUserSub: Subscription = null;
  //#endregion

  private _currentUser: CurrentSessionData = null;
  private _refreshTokenInProgress = false;
  private _refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );

  constructor(
    private _authService: AuthenticationService,
    private _loggerService: LoggerService,
    private _platform: Platform,
    private _store: Store,
    private _dpAuthenticationService: DpAuthenticationService,
    private _themeService: ThemeService
  ) {
    this._selectCurrentUserSub = this._store
      .select(currentUserData)
      .pipe(
        filter((currentUserData: GenericUserData) => currentUserData !== null),
        map((currentUserData: GenericUserData) => currentUserData.currentUser)
      )
      .subscribe((userData: CurrentSessionData) => {
        this._currentUser = new CurrentSessionData();
        this._currentUser = userData;
      });
  }

  ngDestroy() {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "ngDestroy()",
      "DESROY."
    );
    this.unsubscribeStoreSelect();
  }

  /**
   * {@link intercept} will be execute on every HTTP call
   *
   * It's used to popupate header with authorization token and content type. It also verify if api endpoint is correct:
   * there're some case (example pressing keyboard F5),
   * it'll be not not load and empty. In this case, interceptor will populate it reading current endpoint from storage.
   * Another incerpetor usage is for refresh current expired token: when token is expired,
   * api perform an 401 error status (except login that missmatch credential).
   * In this  case will perform refresh token api call and new token is injected
   */
  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    //this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "intercept()", "START 1. THIS IS CALL: " + req.url);

    /**
This flag specifies the time out limit (expresed in MILLISECONDS) for a peculiar HTTP request, if such limit is to be applied.
Default value is specified in the currently used "enviroment" file.
*/
    let httpRequestTimeout: number = environment.httpRequestTimeoutDefault;

    req = this.initializeHeaderRquest(req);

    //this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "intercept()", "START 2. THIS IS CALL: " + req.url);

    const uriValidator: Observable<string> = this.checkIfUrlIsPrence(req.url); //cast promise to observable

    return uriValidator.pipe(
      switchMap((uriComplete: string) => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "intercept()",
          "START 3. THIS IS CALL ORIG.: " +
          req.url +
          " | uriComplete: " +
          uriComplete
        );

        req = req.clone({ url: uriComplete });

        //#region Define time-out period for peculiar Http Request
        //CASE: Call to retrieve complete Supervsion list; it may need an higher timeout limit than default.
        if (uriComplete.includes(AppSettings.API_URL_CUSTOMINTERFACE)) {
          httpRequestTimeout = 40000;
        } else {
          //CASE: Default.

          httpRequestTimeout = environment.httpRequestTimeoutDefault;
        }

        //#endregion
        if (
          uriComplete.includes(AppSettings.API_URL_SYSTEM + "/version")
        ) {
          return next.handle(req);
        }

        if (this._refreshTokenInProgress && !req.url.includes("refreshtoken")) {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "intercept()",
            "TOKEN IS GETTING REFESHED. THIS IS CALL: " + req.url
          );

          return this.awaitForRefreshTokenFinish(req, next);
        }

        /*
        if (req.url.includes("calendar")) {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "intercept()",
            "TCALENDAR CALL!"
          );
        }
        */

        //this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "intercept()", "START 4. THIS IS CALL: " + req.url);

        return next.handle(req).pipe(
          timeout(httpRequestTimeout),
          retryWhen((result: Observable<any>) => {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "httpHandler",
              "handle.pipe()",
              "START 5. Request: " + JSON.stringify(req)
            );
            /*
                        this._loggerService.log(
                          LogLevel.LOG_INFO,
                          this._logContext,
                          "httpHandler",
                          "handle.pipe()",
                          "START. Request headers - Bearer: " + req.headers.get("Authorization")
                        );
            */

            let retryCount = 0;

            return result.pipe(
              tap((httpCall: HttpErrorResponse) => {
                this._loggerService.log(
                  LogLevel.LOG_INFO,
                  this._logContext,
                  "httpHandler",
                  "result.pipe",
                  "Response: " + JSON.stringify(httpCall)
                );
              }),
              tap((r) => {
                retryCount++;
                this._loggerService.log(
                  LogLevel.LOG_INFO,
                  this._logContext,
                  "httpHandler",
                  "result.pipe",
                  " retrying HTTP strategy count: " + retryCount
                );
              }),
              switchMap((httpCall: HttpErrorResponse) => {
                this._loggerService.log(
                  LogLevel.LOG_INFO,
                  this._logContext,
                  "httpHandler",
                  "result.pipe",
                  " retrying HTTP strategy: httpCall.status= " + httpCall.status
                );
                if (httpCall.status === 0 && retryCount <= 2) {
                  this._loggerService.log(
                    LogLevel.LOG_ERROR,
                    this._logContext,
                    "httpHandler",
                    "result.pipe",
                    " RETURN; Server not reachable "
                  );

                  return of(httpCall);
                }

                retryCount = 0;
                this._loggerService.log(
                  LogLevel.LOG_ERROR,
                  this._logContext,
                  "httpHandler",
                  "result.pipe",
                  "httpCall.status NOT 0; server IS reachable, BUT..."
                );
                return throwError(httpCall);
              }),
              delay(2000),
              take(3)
            );
          }),
          catchError((error: HttpErrorResponse) => {
            this.logErrorRequestData(req);
            this._loggerService.log(
              LogLevel.LOG_ERROR,
              this._logContext,
              this._logContext,
              "intercept()",
              "REQUEST ERROR url: " +
              req.url +
              " | code: " +
              error.status +
              " | error: " +
              (error?.message ?? JSON.stringify(error))
            );
            //perform logout request when refresh token api call failed to obtain anything.
            /*  if (error.status === 401 && req.url.includes("refreshtoken")) {
              return this.handleUnthorizedRefreshTokenRequest(error);
            }  */

            /* Here we do nothing. The case where a "getRefreshToken" call receive a 401 error call is hnadled inside the "handle401ErrorResponse()"
            methid, through invocation of "handleUnthorizedRefreshTokenRequest()" immediately after the API call itself.*/
            if (error.status === 401 && req.url.includes("refreshtoken")) {
              this._loggerService.log(
                LogLevel.LOG_ERROR,
                this._logContext,
                this._logContext,
                "intercept()",
                "ERROR 401 in get refreshToken api call: " +
                (error?.message ?? JSON.stringify(error))
              );

              return throwError(error);
            } else if (error.status === 401 && !req.url.includes("login")) {
              return this.handle401ErrorResponse(req, next);
            } else {
              return this.handleGenericRequestError(error);
            }
          })
        );
      })
    );
  }

  /**
   * handle refresh token procedure
   *
   * @param request
   * @param next
   * @returns
   */
  private handle401ErrorResponse(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<any> {
    this._loggerService.log(
      LogLevel.LOG_ERROR,
      this._logContext,
      "httpHandler",
      "handle401ErrorResponse",
      "ERROR. refreshTokenInProgress 2: " + this._refreshTokenInProgress
    );
    // 401 errors are most likely going to be because we have an expired token that we need to refresh.
    if (!this._refreshTokenInProgress) {
      this._refreshTokenInProgress = true;
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        "httpHandler",
        "handle401ErrorResponse",
        "ERROR. refreshTokenInProgress 3: " + this._refreshTokenInProgress
      );
      // Set the refreshTokenSubject to null so that subsequent API calls will wait until the new token has been retrieved
      this._refreshTokenSubject.next(null);

      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        "httpHandler",
        "handle401ErrorResponse",
        "ASKING FOR NEW TOKENS."
      );

      return this.getGenericRefreshToken().pipe(
        switchMap((currentSessionDataRefreshed: CurrentSessionData) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "httpHandler",
            "handle401ErrorResponse",
            "CALL FOR NEW TOKENS COMPLETE. Mew Access Token(Bearer): " +
            currentSessionDataRefreshed.accessToken +
            " New Access Exp: " +
            currentSessionDataRefreshed?.expireTokenAccessDateString +
            " | New Refresh Exp:" +
            currentSessionDataRefreshed?.expireTokenRefreshDateString
          );

          this._refreshTokenInProgress = false;
          this._refreshTokenSubject.next(currentSessionDataRefreshed);

          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "httpHandler",
            "handle401ErrorResponse",
            "Return new TOKEN for URL: " + request.url
          );
          return next.handle(this.initializeHeaderRquest(request));
        }),
        catchError((error) => {
          return this.handleUnthorizedRefreshTokenRequest(error);
        })
      );
    }

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "handle401ErrorResponse()",
      "TOKEN IS GETTING REFESHED. THIS IS CALL: " + request.url
    );

    return this.awaitForRefreshTokenFinish(request, next);
  }

  /**
   * This function calculate refresh token effective function
   *
   * @returns
   */
  private getGenericRefreshToken(): Observable<any> {
    return this.getDigitalPlaceRafreshToken();
  }

  /**
   * Get BIGStudio refresh token
   *
   * @returns
   */
  private getBIGStudioRefreshToken(): Observable<any> {
    return this._authService.getRefreshToken();
  }

  public getDigitalPlaceRafreshToken(): Observable<any> {
    return from(this._dpAuthenticationService.getRefreshToken());
  }

  /**
   * This method is invocked when an API CALL should be executed, but a call of "_authService.getRefreshToken()" has recently been made AND it didn't return yet.
   * This means the auth bearer the API CALL is using shall surely be outdated when its execution will happen.
   * This method shall put the API CALL "on hold", causing its repetition (with a new call of "initializeHeaderRquest()", which shall build an updated auth bearer)
   * when the refreshing procedure shall be terminated.
   * @param request The http-request associated with the API CALL.
   * @param next The HttpHandler object through wich we shall repeat the API CALL.
   * @returns
   */
  private awaitForRefreshTokenFinish(
    request: HttpRequest<any>,
    next: HttpHandler
  ) {
    return this._refreshTokenSubject.pipe(
      filter(
        (currentSessionDataRefreshed) => currentSessionDataRefreshed !== null
      ),
      take(1),
      tap((currentSessionDataRefreshed) => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "awaitForRefreshTokenFinish()",
          "RE-LAUNCHING http-request 2: " + JSON.stringify(request)
        );
      }),
      switchMap((currentSessionDataRefreshed) =>
        next.handle(this.initializeHeaderRquest(request))
      )
    );
  }

  /**
   * Handle unauthorized refresh token data
   *
   * @param error
   * @returns
   */
  private handleUnthorizedRefreshTokenRequest(
    error: HttpErrorResponse
  ): Observable<any> {
    this._loggerService.log(
      LogLevel.LOG_ERROR,
      this._logContext,
      "httpHandler",
      "handleAnthorizedRefreshTokenRequest",
      "Refresh token api call FAILED! Logging out."
    );

    this._refreshTokenInProgress = false;
    this._authService.logout(false);

    return throwError(error);
  }

  /**
   * Handle generic error response for Http request
   *
   * @param error
   * @returns
   */
  private handleGenericRequestError(error: HttpErrorResponse): Observable<any> {
    this._loggerService.log(
      LogLevel.LOG_ERROR,
      this._logContext,
      "httpHandler",
      "handleGenericRequestError",
      "OTHER ERROR: " + error.status
    );

    return throwError(error);
  }

  /**
   * {@link initializeHeaderRquest} initialize header http request
   * 'x-device-native' header is used to detect native usage devices.
   * Since 3.100 version, users native access is blocked if plant hasn't proper license
   *
   * Function that populate header request
   */
  private initializeHeaderRquest(request: HttpRequest<any>): HttpRequest<any> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "initializeHeaderRquest()",
      "INITIALISING HEADER OF: " + request.url
    );

    if (
      this._currentUser === null ||
      this._currentUser === undefined ||
      request.url.includes("login")
    ) {
      request = request.clone({
        headers: new HttpHeaders({
          "Content-Type": "application/json",
        }),
      });
    } else if (
      this._currentUser &&
      (request.url.includes("api/file/upload") ||
        request.url.includes("api/reporthtml/import") ||
        request.url.includes("api/gateway/knx/import") ||
        (request.url.includes("api/media/s3") && request.method === "POST") ||
        (request.url.includes("mqtt/import/schema/") && request.method === "PUT")
      )) {
      request = request.clone({
        headers: new HttpHeaders({
          Authorization: "Bearer " + this._currentUser.accessToken.toString()
        }),
      });
    } else {
      request = request.clone({
        headers: new HttpHeaders({
          "Content-Type": "application/json",
          Authorization: "Bearer " + this._currentUser.accessToken.toString(),
        }),
      });
    }

    return request;
  }

  /**
   * Check if current endpoint is not empty
   *
   * @param uri : uri complete
   */
  private checkIfUrlIsPrence(uri: string): Observable<string> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "checkIfUrlIsPrence()",
      "URI: " + uri
    );

    if (
      !(uri.startsWith("http") || uri.startsWith("https")) &&
      !uri.startsWith(".")
    ) {
      return this._store.select(selectedPlant).pipe(
        // filter((currentPlant: Plant) => currentPlant.Endpoint !== ""),
        take(1),
        map((currentPlant: Plant) => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_store.select(selectedPlant)",
            "checkIfUrlIsPrence()",
            "EMIT ON URI: " + uri
          );
          return currentPlant ? currentPlant.Endpoint + uri : uri;
        })
      );
    } else {
      return of(uri);
    }
  }

  /**
   * Handle full log of request data
   *
   * @param req
   */
  private logErrorRequestData(req): void {
    this._loggerService.log(
      LogLevel.LOG_ERROR,
      this._logContext,
      "httpHandler",
      "logErrorRequestData",
      "ERROR IN REQUEST."
    );
    this._loggerService.log(
      LogLevel.LOG_ERROR,
      this._logContext,
      "httpHandler",
      "logErrorRequestData",
      "- TO URL: " + req.url
    );
    this._loggerService.log(
      LogLevel.LOG_ERROR,
      this._logContext,
      "httpHandler",
      "logErrorRequestData",
      "- WITH HEADER: " + JSON.stringify(req.headers)
    );
    this._loggerService.log(
      LogLevel.LOG_ERROR,
      this._logContext,
      "httpHandler",
      "logErrorRequestData",
      "- WITH BODY: " + JSON.stringify(req.body)
    );

    this._loggerService.log(
      LogLevel.LOG_ERROR,
      this._logContext,
      "httpHandler",
      "logErrorRequestData",
      "- currentUser: " + JSON.stringify(this._currentUser)
    );

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      "httpHandler",
      "logErrorRequestData",
      "ERROR. refreshTokenInProgress 1: " + this._refreshTokenInProgress
    );
  }

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    if (this._selectCurrentUserSub) {
      this._selectCurrentUserSub.unsubscribe();
    }
  }
  //#endregion
}
